import { useId } from 'preact/hooks'
import { ArrowRight, CircleCheck, CircleDashed, CircleX, Globe, Info, Loader2 } from 'lucide-preact'
import type { SendingDomainViewDto, SendingDomainWarmupSummaryDto } from '@/types/sendingDomains'
import { routes } from '@/utils/routes'
import {
  COMPANY_WEBSITE_PLACE,
  checkedAtPhrase,
  dnsCheckOutcome,
  dnsCheckSentence,
  recommendedDomainStatus,
  recommendedSubdomain,
  redirectSteps,
  regionLabel,
  setupVerdict,
  subdomainTradeOff,
  subdomainsPassSentence,
  warmsUpOncePassedSentence,
  websiteCheckStatus,
  websiteFailedSentence,
  websiteNotCheckedSentence,
} from '@/utils/sendingDomainStatus'
import { CheckItem, Chip, CopyRuns, DomainStatusBox, type StatusFact } from './DomainPageParts'
import { DomainStatePill } from './DomainStatePill'
import { DeveloperInstructions } from './DeveloperInstructions'
import { TEXT_LINK } from './styles'

interface DomainSetupViewProps {
  clientCompanyId: string
  domain: SendingDomainViewDto
  domains: SendingDomainViewDto[]
  companyDomain: string | null
  summary: SendingDomainWarmupSummaryDto | null
  /** Whether the DNS status is being polled right now. */
  checking: boolean
  onOpenDomain: (domain: SendingDomainViewDto) => void
  onSaved: (domain: SendingDomainViewDto) => void
}

/** A domain still setting up: what stops it sending, one check at a time, and how to get each one passing. */
export function DomainSetupView({ clientCompanyId, domain, domains, companyDomain, summary, checking, onOpenDomain, onSaved }: DomainSetupViewProps) {
  const headingId = useId()
  const websiteFailed = websiteCheckStatus(domain) === 'Failed'
  const recommended = websiteFailed ? recommendedSubdomain(domains, domain, companyDomain) : null
  const verdict = setupVerdict(domain, companyDomain, recommended?.Domain ?? null, COMPANY_WEBSITE_PLACE.settings)

  const facts: StatusFact[] = [
    { label: 'Status', value: <DomainStatePill domain={domain} /> },
    { label: 'Region', value: regionLabel(domain.Region) },
    {
      label: 'Company website',
      value: companyDomain ?? <a href={routes.settingsCompany(clientCompanyId)} className={TEXT_LINK}>Set it in Company settings</a>,
    },
    { label: 'Last checked', value: domain.LastCheckedUtc ? checkedAtPhrase(domain.LastCheckedUtc) : 'Not checked yet' },
  ]

  return (
    <div className="space-y-10">
      <DomainStatusBox pill="SettingUp" verdict={verdict} facts={facts} />

      <section aria-labelledby={headingId} className="space-y-6">
        <h3 id={headingId} className="text-base font-semibold text-slate-900 dark:text-white">Before {domain.Domain} can send</h3>
        <DnsCheck domain={domain} checking={checking} />
        <WebsiteCheck
          clientCompanyId={clientCompanyId}
          domain={domain}
          companyDomain={companyDomain}
          recommended={recommended}
          summary={summary}
          onOpenDomain={onOpenDomain}
        />
        <div className="pt-2">
          <DeveloperInstructions clientCompanyId={clientCompanyId} domain={domain} companyDomain={companyDomain} onSent={onSaved} />
        </div>
      </section>
    </div>
  )
}

const PASSED_ICON = <CircleCheck className="h-5 w-5 text-green-600 dark:text-green-400" aria-hidden="true" />
const FAILED_ICON = <CircleX className="h-5 w-5 text-red-600 dark:text-red-400" aria-hidden="true" />
const PENDING_ICON = <CircleDashed className="h-5 w-5 text-amber-600 dark:text-amber-400" aria-hidden="true" />

function DnsCheck({ domain, checking }: { domain: SendingDomainViewDto; checking: boolean }) {
  const outcome = dnsCheckOutcome(domain)
  if (!outcome) return null
  const detail = <p className="mt-0.5 max-w-prose text-[13px] text-slate-600 dark:text-slate-400">{dnsCheckSentence(domain, outcome)}</p>

  if (outcome === 'Passed') {
    return <CheckItem icon={PASSED_ICON} title="DNS records" chip={<Chip tone="green">Passed</Chip>}>{detail}</CheckItem>
  }
  if (outcome === 'Failed') {
    return <CheckItem icon={FAILED_ICON} title="DNS records" chip={<Chip tone="red">Failed</Chip>}>{detail}</CheckItem>
  }
  return (
    <CheckItem icon={PENDING_ICON} title="DNS records" chip={<Chip tone="amber">Pending</Chip>}>
      {detail}
      {domain.StatusMessage && <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{domain.StatusMessage}</p>}
      {checking && (
        <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> Checking…
        </p>
      )}
    </CheckItem>
  )
}

function WebsiteCheck({ clientCompanyId, domain, companyDomain, recommended, summary, onOpenDomain }: {
  clientCompanyId: string
  domain: SendingDomainViewDto
  companyDomain: string | null
  recommended: SendingDomainViewDto | null
  summary: SendingDomainWarmupSummaryDto | null
  onOpenDomain: (domain: SendingDomainViewDto) => void
}) {
  const detail = 'mt-0.5 max-w-prose text-[13px] text-slate-600 dark:text-slate-400'
  const status = websiteCheckStatus(domain)
  const name = domain.Domain

  if (status === 'Passed') {
    return (
      <CheckItem icon={PASSED_ICON} title="Website check" chip={<Chip tone="green">Passed</Chip>}>
        {domain.WebsiteCheckDetail && <p className={detail}>{domain.WebsiteCheckDetail}</p>}
      </CheckItem>
    )
  }
  if (status === 'NotChecked') {
    return (
      <CheckItem icon={PENDING_ICON} title="Website check" chip={<Chip tone="slate">Not checked yet</Chip>}>
        <p className={detail}>{websiteNotCheckedSentence(name, companyDomain)}</p>
      </CheckItem>
    )
  }
  if (status !== 'Failed') return null

  return (
    <CheckItem icon={FAILED_ICON} title="Website check" chip={<Chip tone="red">Failed</Chip>}>
      <div className="space-y-5">
        {companyDomain ? (
          <p className={detail}>{websiteFailedSentence(name, companyDomain)}</p>
        ) : (
          <p className={detail}>
            Set the company's Website in <a href={routes.settingsCompany(clientCompanyId)} className={TEXT_LINK}>Company settings</a>,
            so the check knows where {name} should lead.
          </p>
        )}

        {(domain.WebsiteLandedOnUrl || domain.WebsiteCheckDetail) && (
          <dl className="grid gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 sm:grid-cols-2 dark:border-slate-700 dark:bg-slate-800/50">
            {domain.WebsiteLandedOnUrl && (
              <div>
                <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Landed on</dt>
                <dd className="mt-1 break-all font-mono text-[13px] text-slate-800 dark:text-slate-100">{domain.WebsiteLandedOnUrl}</dd>
              </div>
            )}
            {domain.WebsiteCheckDetail && (
              <div>
                <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Response</dt>
                <dd className="mt-1 text-sm text-slate-800 dark:text-slate-100">{domain.WebsiteCheckDetail}</dd>
              </div>
            )}
          </dl>
        )}

        {recommended && companyDomain && (
          <Recommendation recommended={recommended} companyDomain={companyDomain} summary={summary} onOpenDomain={onOpenDomain} />
        )}

        {companyDomain && (
          <KeepSteps name={name} companyDomain={companyDomain} hasAlternative={!!recommended} summary={summary} />
        )}
      </div>
    </CheckItem>
  )
}

function Recommendation({ recommended, companyDomain, summary, onOpenDomain }: {
  recommended: SendingDomainViewDto
  companyDomain: string
  summary: SendingDomainWarmupSummaryDto | null
  onOpenDomain: (domain: SendingDomainViewDto) => void
}) {
  const headingId = useId()
  const tradeOff = subdomainTradeOff(companyDomain)
  return (
    <section aria-labelledby={headingId} className="space-y-2 rounded-lg border border-slate-200 px-5 py-4 dark:border-slate-700">
      <h5 id={headingId} className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
        <Globe className="h-4 w-4 text-slate-500" aria-hidden="true" />
        Recommended: send from {recommended.Domain}
      </h5>
      <p className="max-w-prose text-sm text-slate-600 dark:text-slate-400">
        {recommendedDomainStatus(recommended, summary?.RateLimits ?? null)} {subdomainsPassSentence(companyDomain)}{' '}
        <span className="font-medium text-slate-800 dark:text-slate-200">{tradeOff.lead}</span> {tradeOff.text}
      </p>
      <button type="button" onClick={() => onOpenDomain(recommended)} className={`inline-flex items-center gap-1 text-sm ${TEXT_LINK}`}>
        Open {recommended.Domain} <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </section>
  )
}

function KeepSteps({ name, companyDomain, hasAlternative, summary }: {
  name: string
  companyDomain: string
  hasAlternative: boolean
  summary: SendingDomainWarmupSummaryDto | null
}) {
  const headingId = useId()
  const number = 'flex h-5 w-5 flex-none items-center justify-center rounded-full bg-slate-100 text-xs font-semibold tabular-nums text-slate-600 dark:bg-slate-800 dark:text-slate-300'
  const onceItPasses = summary ? warmsUpOncePassedSentence(name, summary.Steps) : ''
  return (
    <section aria-labelledby={headingId} className="space-y-2.5">
      <h5 id={headingId} className="text-sm font-semibold text-slate-900 dark:text-white">
        {hasAlternative ? `Or keep ${name}` : `To keep ${name}`}
      </h5>
      <ol className="space-y-2">
        {redirectSteps(name, companyDomain).map((runs, index) => (
          <li key={index} className="flex max-w-prose items-start gap-2.5 text-sm text-slate-700 dark:text-slate-300">
            <span className={number} aria-hidden="true">{index + 1}</span>
            <span><CopyRuns runs={runs} /></span>
          </li>
        ))}
      </ol>
      {onceItPasses && (
        <p className="flex max-w-prose items-start gap-2 pt-1 text-[13px] text-slate-600 dark:text-slate-400">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden="true" />
          <span>{onceItPasses}</span>
        </p>
      )}
    </section>
  )
}
